import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  Row,
} from "react-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import "./breadcrumb.css";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  RedditShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
  TelegramIcon,
  RedditIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBookmark,
  faHeart,
  faPrint,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { addToFavourite, getFavorite } from "../../../redux/feedback/action";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { helperFunction } from "../../../utils/helperFunctions";
import ReportPageModal from "../reportPageModal/reportPageModal";
import { faCopyright } from "@fortawesome/free-regular-svg-icons";
import { useTranslation } from "react-i18next";

/**
 * Crumb items:
 * - Legacy: { label, link? } — label is shown as-is.
 * - Centralized: { id, link? } — label comes from translation `breadcrumb.<id>` (add once in locale files).
 * - Explicit key: { labelKey, link? } — label from `t(labelKey)`; optional `label` as defaultValue if key missing.
 */
const resolveCrumbLabel = (crumb, t) => {
  if (crumb.labelKey) {
    return t(crumb.labelKey, {
      defaultValue: crumb.label ?? crumb.labelKey,
    });
  }
  if (crumb.id != null && String(crumb.id).trim() !== "") {
    return t(`breadcrumb.${crumb.id}`, {
      defaultValue: crumb.label ?? crumb.id,
    });
  }
  return crumb.label ?? "";
};

const BreadcrumbSec = ({ crumbs = [] }) => {
  const { t } = useTranslation();
  const shareUrl = window.location.href;
  const shareTitle = "Check out this feedback page!";
  const dispatch = useDispatch();
  const userId = helperFunction.getUserInfo();
  const feedback = useSelector((state) => state.reviews);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      const payload = { userId, pageUrl: shareUrl };
      dispatch(getFavorite(payload));
    }
  }, []);
  const toggleFavourite = () => {
    if (!userId) {
      toast.error("Please login first to add to favourites.");
      return;
    }

    const payload = {
      userId: userId,
      pageUrl: shareUrl,
    };
    dispatch(addToFavourite(payload));
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success("Copied to clipboard successfully!"))
      .catch(() => toast.error("Failed to copy URL."));
  };

  const printPage = () => {
    window.print();
  };

  const toggleBookmark = () => {
    const title = document.title;
    const url = window.location.href;

    if (window.sidebar && window.sidebar.addPanel) {
      window.sidebar.addPanel(title, url, "");
      toast.success("Bookmark added successfully!");
    } else if (window.external && "AddFavorite" in window.external) {
      window.external.AddFavorite(url, title);
      toast.success("Bookmark added successfully!");
    } else if (window.chrome) {
      toast.info(
        "Press " +
          (navigator.userAgent.indexOf("Mac") !== -1 ? "Command/Cmd" : "Ctrl") +
          " + D to bookmark this page.",
      );
    } else {
      toast.info(
        "Your browser does not support adding bookmarks. Please use the bookmark feature in your browser.",
      );
    }
  };

  return (
    <section className="breadcrumb-sec">
      <Container>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          {/* <Col md={6} data-aos="fade-right" data-aos-delay="200"> */}
          <Breadcrumb>
            {crumbs.map((crumb, idx) => {
              const text = resolveCrumbLabel(crumb, t);
              return crumb.link && idx !== crumbs.length - 1 ? (
                <Breadcrumb.Item
                  key={idx}
                  linkAs={Link}
                  linkProps={{ to: crumb.link }}
                >
                  {text}
                </Breadcrumb.Item>
              ) : (
                <Breadcrumb.Item key={idx} active>
                  {text}
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
          {/* </Col>

          <Col
            md={6}
            data-aos="fade-left"
            data-aos-delay="200"
            className="text-end"
          > */}
          <ul className="icon-list list-unstyled d-inline-flex flex-wrap flex-grow-1 align-items-center justify-content-end mb-0">
            <li
              onClick={toggleFavourite}
              className="position-relative tooltip-parent"
            >
              <FontAwesomeIcon
                icon={faHeart}
                className={
                  feedback?.favorite?.isFavorite ? "heart-icon red" : ""
                }
              />
              <span className="tooltip-text">Add to Favourite</span>
            </li>
            <li className="share-dropdown position-relative tooltip-parent">
              <Dropdown as={ButtonGroup} drop="down-centered" autoClose={true}>
                <Dropdown.Toggle
                  variant="none"
                  id="dropdown-autoclose-true"
                  className="m-0"
                >
                  <FontAwesomeIcon icon={faShare} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <div className="list-unstyled d-flex flex-column gap-2">
                    <div className="icon-wrapper text-center">
                      <FacebookShareButton url={shareUrl} quote={shareTitle}>
                        <FacebookIcon size={32} round />
                      </FacebookShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <TwitterShareButton url={shareUrl} title={shareTitle}>
                        <TwitterIcon size={32} round />
                      </TwitterShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <LinkedinShareButton url={shareUrl} title={shareTitle}>
                        <LinkedinIcon size={32} round />
                      </LinkedinShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <EmailShareButton url={shareUrl} title={shareTitle}>
                        <EmailIcon size={32} round />
                      </EmailShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <WhatsappShareButton url={shareUrl} title={shareTitle}>
                        <WhatsappIcon size={32} round />
                      </WhatsappShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <TelegramShareButton url={shareUrl} title={shareTitle}>
                        <TelegramIcon size={32} round />
                      </TelegramShareButton>
                    </div>
                    <div className="icon-wrapper text-center">
                      <RedditShareButton url={shareUrl} title={shareTitle}>
                        <RedditIcon size={32} round />
                      </RedditShareButton>
                    </div>
                    <div className="icon-wrapper copy-icon text-center">
                      <button
                        className="p-0 m-0 border-0 bg-transparent"
                        onClick={copyToClipboard}
                      >
                        <FontAwesomeIcon
                          icon={faCopy}
                          fontSize={26}
                          color="#171717"
                        />
                      </button>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <span className="tooltip-text">Share this page</span>
            </li>
            <li
              onClick={printPage}
              className="position-relative tooltip-parent"
            >
              <FontAwesomeIcon icon={faPrint} />
              <span className="tooltip-text">Print this page</span>
            </li>
            <li
              onClick={toggleBookmark}
              className="position-relative tooltip-parent"
            >
              <FontAwesomeIcon icon={faBookmark} />
              <span className="tooltip-text">Add to Bookmark</span>
            </li>
            <li
              onClick={() => setIsReportModalOpen(true)}
              className="position-relative tooltip-parent"
            >
              <FontAwesomeIcon icon={faBan} />
              <span className="tooltip-text">Report Link</span>
            </li>
            <ReportPageModal
              show={isReportModalOpen}
              onHide={() => setIsReportModalOpen(false)}
              userId={userId}
              url={window.location.href}
            />
          </ul>
        </div>
      </Container>
    </section>
  );
};

export default BreadcrumbSec;
